import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  householdMemberInsertSchema,
  householdMemberRelationshipsInsertSchema,
  householdMemberRelationshipsSchema,
  householdMemberRelationshipsUpdateSchema,
  householdMemberUpdateSchema,
} from "@libs/db-schemas";

import { HTTPStatusCode } from "../../types";
import { errorSchema } from "../../utils/errors";
import { householdMemberWithCoverageSchema } from "./schemas";

const c = initContract();

export const householdMembersContract = c.router({
  listHouseholdMembers: {
    summary: "List household members for the logged in patient",
    method: "GET",
    path: "/household-members",
    responses: {
      [HTTPStatusCode.OK]: z.array(
        householdMemberWithCoverageSchema.extend({
          householdMemberRelationship: householdMemberRelationshipsSchema
            .pick({
              livingWithStatus: true,
              relationship: true,
              otherRelationship: true,
              taxRelationship: true,
            })
            .nullish(),
        }),
      ),
      [HTTPStatusCode.INTERNAL_SERVER_ERROR]: errorSchema,
    },
  },
  getHouseholdMember: {
    summary: "Get a household member by ID",
    method: "GET",
    path: "/household-members/:id",
    pathParams: z.object({
      id: z.string(),
    }),
    responses: {
      [HTTPStatusCode.OK]: householdMemberWithCoverageSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
      [HTTPStatusCode.FORBIDDEN]: errorSchema,
    },
  },
  updateHouseholdMember: {
    summary: "Update an existing household member by ID",
    method: "PATCH",
    path: "/household-members/:id",
    pathParams: z.object({
      id: z.uuid(),
    }),
    body: householdMemberUpdateSchema.extend({
      householdMemberRelationship: householdMemberRelationshipsUpdateSchema
        .pick({
          livingWithStatus: true,
          relationship: true,
          otherRelationship: true,
          taxRelationship: true,
        })
        .nullish(),
    }),
    responses: {
      [HTTPStatusCode.OK]: householdMemberWithCoverageSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
      [HTTPStatusCode.FORBIDDEN]: errorSchema,
    },
  },
  addHouseholdMember: {
    summary:
      "Add a new household member and its relationship for the logged in patient",
    method: "POST",
    path: "/household-members",
    body: householdMemberInsertSchema
      .pick({
        firstName: true,
        lastName: true,
        dateOfBirth: true,
        ageRange: true,
        maritalStatus: true,
        taxFilingStatus: true,
        mainAddressCity: true,
        mainAddressState: true,
        mainAddressStreetOne: true,
        mainAddressStreetTwo: true,
        mainAddressComplement: true,
        mainAddressZip: true,
        mainCounty: true,
        mailingAddressCity: true,
        mailingAddressState: true,
        mailingAddressStreetOne: true,
        mailingAddressStreetTwo: true,
        mailingAddressComplement: true,
        mailingAddressZip: true,
        mailingCounty: true,
        childCaregiverStatus: true,
        ssn: true,
        medicaidSubscriberId: true,
      })
      .extend({
        householdMemberRelationship:
          householdMemberRelationshipsInsertSchema.pick({
            livingWithStatus: true,
            relationship: true,
            otherRelationship: true,
            taxRelationship: true,
          }),
      }),
    responses: {
      [HTTPStatusCode.CREATED]: householdMemberWithCoverageSchema,
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
    },
  },
  getCurrentHouseholdMember: {
    summary: "Get the current authenticated household member",
    method: "GET",
    path: "/household-members/me",
    responses: {
      [HTTPStatusCode.OK]: householdMemberWithCoverageSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
    },
  },
  deleteHouseholdMember: {
    path: "/household-members/:id",
    summary: "Delete a household member by ID",
    method: "DELETE",
    pathParams: z.object({
      id: z.uuid(),
    }),
    responses: {
      [HTTPStatusCode.OK]: c.noBody(),
      [HTTPStatusCode.FORBIDDEN]: errorSchema,
    },
  },
});
